<template>
  <div>
    <container
      :isModal="true"
      @close-modal="cancelSelection()"
      :loading="!stateReady"
    >
      <template v-slot:header>
        <h3 v-text="titulo" />
      </template>
      <template v-slot:body>
        <div v-if="stateReady">
          <div class="rntrc-aviso rntrc-aviso--info p-mb-3">
            <i class="fas fa-info-circle p-mr-2" />
            Procure pelo nome ou pelo CPF/CNPJ quem já foi atendido: o cadastro
            da ANTT e os transportadores da GovPass da sua base.
          </div>
          <form @submit.prevent="buscar()">
            <fieldset :disabled="stateLoading">
              <div class="p-formgrid p-grid p-ai-end">
                <div class="p-field p-fluid p-col-12 p-md-8">
                  <input-with-label label="Nome ou CPF/CNPJ" v-slot="l">
                    <InputText
                      :id="l.id"
                      v-model="termo"
                      maxlength="250"
                      v-focus
                    />
                  </input-with-label>
                </div>
                <div class="p-field p-col-12 p-md-4">
                  <Button
                    type="submit"
                    icon="pi pi-search"
                    label="Buscar"
                    class="p-button-rounded"
                    :loading="stateLoading"
                  />
                </div>
              </div>
            </fieldset>
          </form>
          <DataTable
            v-if="buscou"
            :value="resultados"
            class="p-datatable-sm"
            responsiveLayout="stack"
            breakpoint="40rem"
            :stripedRows="true"
          >
            <template #empty
              >Ninguém encontrado com esse nome ou CPF/CNPJ. Feche e use "Novo
              {{ papelMinusculo }}" para cadastrar.</template
            >
            <Column header="CPF/CNPJ">
              <template #body="{ data }">
                <b v-text="formatCpfCnpj(data.cpfCnpj)" />
              </template>
            </Column>
            <Column field="nome" header="Nome ou razão social" />
            <Column header="Cadastro">
              <template #body="{ data }">
                <span
                  class="aet-badge"
                  :class="
                    data.origem === 'ANT'
                      ? 'aet-badge--success'
                      : 'aet-badge--info'
                  "
                  v-text="data.origem === 'ANT' ? 'ANTT' : 'GovPass'"
                />
              </template>
            </Column>
            <Column style="width: 11rem; text-align: right">
              <template #body="{ data }">
                <Button
                  type="button"
                  icon="pi pi-check"
                  label="Usar"
                  class="p-button-rounded p-button-sm"
                  @click="confirmSelection(data)"
                />
              </template>
            </Column>
          </DataTable>
          <small class="rntrc-busca-limite" v-if="resultados.length >= 20"
            >Mostrando os 20 primeiros: refine a busca para achar outro.</small
          >
        </div>
      </template>
    </container>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  PropType,
  Ref
} from "vue";
import { useToast } from "../../../modules/message";
import { ClientePayload } from "../../../modules/business/cliente/clienteBusiness";
import {
  PessoaAnttPayload,
  usePessoaAnttService
} from "../../../modules/business/rntrc/pessoa/pessoaAnttBusiness";
import { formatCpfCnpj } from "../../../modules/business/rntrc/rntrcTypes";

/** Menos que isso, a API nem busca (traria a base inteira). */
const MINIMO_BUSCA = 3;

interface BuscaPessoaAnttState {
  termo: string;
  resultados: Array<PessoaAnttPayload>;
  buscou: boolean;
  stateReady: boolean;
  stateId: string;
  loadings: Array<Ref<boolean>>;
}

/**
 * Lupa do transportador nas telas de serviço: busca na base do próprio cliente (cadastro da
 * ANTT e transportadores da GovPass) quem já foi atendido, para não cadastrar de novo.
 */
export default defineComponent({
  setup(props, { emit }) {
    const state = reactive<BuscaPessoaAnttState>({
      termo: "",
      resultados: [],
      buscou: false,
      stateReady: false,
      stateId:
        "state-" +
        Math.random()
          .toString(36)
          .substring(2),
      loadings: []
    });

    const stateLoading = computed(
      () => state.loadings.length !== 0 || !!props.parentLoading
    );

    const papelMinusculo = computed(() =>
      (props.papel || "transportador").toLowerCase()
    );

    const titulo = computed(() => "Buscar " + papelMinusculo.value);

    const removeLoading = (loading: Ref<boolean>) => {
      const idx = state.loadings.indexOf(loading);
      if (idx >= 0) {
        state.loadings.splice(idx, 1);
      }
    };

    const buscar = () => {
      const termo = (state.termo ?? "").trim();
      if (termo.length < MINIMO_BUSCA) {
        useToast().warning(
          "Digite pelo menos " +
            MINIMO_BUSCA +
            " letras do nome ou números do CPF/CNPJ."
        );
        return;
      }
      const {
        data,
        loading,
        buscarPessoasParaPreenchimento
      } = usePessoaAnttService();
      state.loadings.push(loading);
      buscarPessoasParaPreenchimento(termo, props.cliente?.idCliente ?? null)
        .then(() => {
          removeLoading(loading);
          state.resultados = (data.value as Array<PessoaAnttPayload>) ?? [];
          state.buscou = true;
        })
        .catch(() => removeLoading(loading));
    };

    const confirmSelection = (pessoa: PessoaAnttPayload) => {
      emit("confirm-selection", pessoa);
    };

    const cancelSelection = () => {
      emit("cancel-selection");
    };

    onMounted(() => {
      state.termo = props.termoInicial ?? "";
      state.stateReady = true;
      if (state.termo.trim().length >= MINIMO_BUSCA) {
        buscar();
      }
    });

    return {
      stateLoading,
      papelMinusculo,
      titulo,
      buscar,
      confirmSelection,
      cancelSelection,
      formatCpfCnpj,
      ...toRefs(state)
    };
  },
  props: {
    cliente: Object as PropType<ClientePayload>,
    /** Papel na tela que abriu: "Transportador", "Arrendatário"... */
    papel: String,
    termoInicial: String,
    parentLoading: Boolean
  },
  emits: ["confirm-selection", "cancel-selection"]
});
</script>

<style scoped lang="scss">
.rntrc-aviso {
  padding: $space-3 $space-4;
  border-radius: $radius-md;
  border: 1px solid $border-color-light;
  font-size: $font-size-sm;

  &--info {
    background: $info-light;
    border-left: 4px solid $info;
  }
}

.rntrc-busca-limite {
  color: $text-secondary;
}
</style>
